import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Library, Play, Save, Trash2, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { LEAF_META, leafLabel } from "../lib/navLayout";
import { NAV as FIN_NAV } from "../finance/FinanceApp";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usePages = () => {
  const { t } = useLang();
  return useMemo(() => [
    ...Object.keys(LEAF_META).map((k) => ({ key: `dash:${k}`, group: "لوحة التحكم", label: leafLabel(k, null, t) })),
    ...FIN_NAV.map((n) => ({ key: `fin:${n.to.replace(/^\/finance\/?/, "") || "dashboard"}`, group: "البرنامج المحاسبي", label: n.label })),
    { key: "portal", group: "بوابة الموظف", label: "بوابة الموظف" },
  ], [t]);
};

const Row = ({ p, cur, onSaved, onPreview }) => {
  const [url, setUrl] = useState(cur ? `https://youtu.be/${cur.video_id}` : "");
  const [title, setTitle] = useState(cur?.title || "");
  const save = async () => {
    try { await axios.put(`${API}/tutorials/${encodeURIComponent(p.key)}`, { url, title }); toast.success(`تم حفظ فيديو «${p.label}»`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
  };
  const del = async () => {
    if (!window.confirm(`حذف فيديو «${p.label}»؟`)) return;
    try { await axios.delete(`${API}/tutorials/${encodeURIComponent(p.key)}`); toast.success(`تم حذف فيديو «${p.label}»`); setUrl(""); setTitle(""); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  return (
    <tr className="border-t border-slate-100 align-middle" data-testid={`tut-lib-row-${p.key}`}>
      <td className="px-2 py-2">{cur ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-rose-400" />}</td>
      <td className="px-2 py-2"><p className="text-sm font-semibold text-slate-800">{p.label}</p><p className="text-[10px] text-slate-400">{p.group}</p></td>
      <td className="px-2 py-2"><input value={url} onChange={(e) => setUrl(e.target.value)} dir="ltr" placeholder="https://youtu.be/…" className="h-8 w-full min-w-[180px] rounded-md border border-slate-200 px-2 text-xs" data-testid={`tut-lib-url-${p.key}`} /></td>
      <td className="px-2 py-2"><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="العنوان" data-testid={`tut-lib-title-${p.key}`} className="h-8 w-full min-w-[140px] rounded-md border border-slate-200 px-2 text-xs" /></td>
      <td className="px-2 py-2"><div className="flex gap-1">
        <button onClick={save} disabled={!url.trim()} title="حفظ" className="grid h-8 w-8 place-items-center rounded-md bg-rose-600 text-white disabled:opacity-40" data-testid={`tut-lib-save-${p.key}`}><Save className="h-3.5 w-3.5" /></button>
        {cur && <button onClick={() => onPreview(cur)} title="معاينة" className="grid h-8 w-8 place-items-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid={`tut-lib-preview-${p.key}`}><Play className="h-3.5 w-3.5" /></button>}
        {cur && <button onClick={del} title="حذف" className="grid h-8 w-8 place-items-center rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`tut-lib-delete-${p.key}`}><Trash2 className="h-3.5 w-3.5" /></button>}
      </div></td>
    </tr>
  );
};

// Owner: all system pages with their tutorial video status
export const TutorialLibraryBody = ({ items, onSaved, onPreview }) => {
  const pages = usePages();
  const [missing, setMissing] = useState(false);
  const [q, setQ] = useState("");
  const done = pages.filter((p) => items[p.key]).length;
  const list = pages.filter((p) => (!missing || !items[p.key]) && (!q.trim() || p.label.includes(q.trim()) || p.group.includes(q.trim())));
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">لها فيديو <b className="text-emerald-600">{done}</b> من {pages.length} صفحة — الناقصة <b className="text-rose-600" data-testid="tut-lib-missing-count">{pages.length - done}</b></p>
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن صفحة…" className="h-9 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm" data-testid="tut-lib-search" />
        <label className="flex items-center gap-1.5 text-sm text-slate-700"><input type="checkbox" checked={missing} onChange={(e) => setMissing(e.target.checked)} data-testid="tut-lib-missing-only" /> الناقصة فقط</label>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-start"><tbody>
          {list.map((p) => <Row key={`${p.key}:${items[p.key]?.video_id || ""}`} p={p} cur={items[p.key]} onSaved={onSaved} onPreview={onPreview} />)}
        </tbody></table>
        {!list.length && <p className="p-6 text-center text-sm text-slate-400">لا توجد صفحات</p>}
      </div>
    </div>
  );
};

export const TutorialLibrary = ({ open, onOpenChange, items, onSaved, onPreview }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent dir="rtl" className="w-[96vw] max-w-4xl max-h-[90dvh] overflow-y-auto" data-testid="tut-library">
      <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2 ps-8"><Library className="h-5 w-5 text-rose-600" /> مكتبة الفيديوهات التعليمية</DialogTitle>
        <DialogDescription className="sr-only">الفيديوهات التعليمية</DialogDescription></DialogHeader>
      <TutorialLibraryBody items={items} onSaved={onSaved} onPreview={onPreview} />
    </DialogContent>
  </Dialog>
);
